import Loader from "@/javascript/components/loader"
import { slideTransition } from "@/javascript/components/animations/slide"
import resized from "@/javascript/components/events/resized"
import { createLogger, nodeToString } from "@/javascript/utils/logging";

// Provides toggling functionality
//
// Place data-toggle='target' on a link (e.g. `a data-toggle='.show-this' Click Me`)
//
// The toggler will toggle (add/remove) the class `open` on the link and the target
// It is up to the user to implement the CSS styles to do the any appropriate hiding,
// showing or any other desired transition. A common pattern is:
//
// data-toggle-within : Scopo the element to be toggled within a parent element
//
// .target {
//   .opened {
//     display: none;
//   }
//
//   .closed {
//     display: block;
//   }
//
//   &.open {
//     .opened {
//       display: block;
//     }
//
//     .closed {
//       display: none;
//     }
//   }
// }
//
// a data-toggle='.target'
//
// .target
//   .opened I'm open
//   .closed I'm closed

export class Toggler {
  constructor (elem, container, onChange, opts = {}) {
    this.logger = createLogger('Toggler')

    this.elem = elem
    this.container = container
    this.always = opts.always
    this.opts = Object.assign({}, this._defaultOptions(), opts)
    this.onChange = onChange || function () {}
    this._bind()

    this.logger.debug(`Toggler: ${nodeToString(this.container)} created for ${nodeToString(this.elem)} with opts: ${JSON.stringify(this.opts)}`)
  }

  set (visible) {
    if(visible === this.isOpen) {
      this.logger.debug(`Toggler: ${nodeToString(this.container)} already visible (from ${nodeToString(this.container)})`)
      return
    }
    if(this.opts.pureCss != 'true'){
      this.logger.debug(`Toggler: ${nodeToString(this.container)} set(${visible}) (from ${nodeToString(this.container)})`)

      const animation = slideTransition(this.container, this._animationMilliseconds(), () => {
        this.toggleClasses(visible)
      })
      animation.then(() => {
        this.onToggleComplete()
      })
      this.onChange(visible, animation, this.elem, this.container)
    } else {
      this.logger.debug(`Toggler (pureCSS): ${nodeToString(this.container)} set(${visible}) (from ${nodeToString(this.container)})`)

      this.toggleClasses(visible)
      if (!this.container.togglerTransitionEndBound) {
        this.container.addEventListener('transitionend', (event) => {
          if(event.target !== this.container) {
            return
          }
          this.onPureCssToggleComplete()
        })
        this.container.togglerTransitionEndBound = true
      }

      if (!this.container.togglerAnimationEndBound) {
        this.container.addEventListener('animationend', (event) => {
          if(event.target !== this.container) {
            return
          }
          this.onPureCssToggleComplete()
        })
        this.container.togglerAnimationEndBound = true
      }

      let animationDuration = window.getComputedStyle(this.container,null).getPropertyValue('animation-duration')
      let transitionDuration = window.getComputedStyle(this.container,null).getPropertyValue('transition-duration')

      if(parseFloat(animationDuration) <= 0 && parseFloat(transitionDuration) <= 0) {
        this.onPureCssToggleComplete()
      }

      this.onChange(visible, null, this.elem, this.container)
    }
  }

  onPureCssToggleComplete () {
    resized(this.container)
    this.onToggleComplete()
  }

  toggleClasses (visible) {
    this.isOpen = visible
    if(this.isOpen) {
      this.container.classList.add('open')
      this.elem.classList.add('open')
    } else {
      this.elem.classList.remove('open')
      this.container.classList.remove('open')
    }
  }

  onToggleComplete () {
    const scrollTarget = document.querySelector(this.opts.scrollTarget)
    if(scrollTarget) {
      scrollTarget.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"})
    }
    this.elem.dispatchEvent(new Event('elementToggled', {bubbles: true, cancelable: false, element: this.elem }));
  }

  toggle () {
    if(this.always === 'close') {
      this.isOpen=true
    }
    if(this.always ==='open') {
      this.isOpen=false
    }
    if(!this.always) {
      this.isOpen = this.container.classList.contains('open')
    }
    this.set(!this.isOpen)
  }


  _bind () {
    this.elem.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.toggle()
    })
  }

  _defaultOptions () {
    return {
      animation: 200
    }
  }

  _animationMilliseconds () {
    let milliseconds

    if(this.opts.animation === 'false') {
      milliseconds = 0
    } else {
      milliseconds = this.opts.animation || 200
    }

    return milliseconds
  }
}

class RadioToggler extends Toggler {
  _bind () {
    document.querySelectorAll(`input[type='radio'][name='${this.elem.name}']`)
      .forEach((radio) => {
        const toggler = this
        radio.addEventListener('change', function (e) {
          toggler.logger.debug(`RadioToggler: change event for ${nodeToString(toggler.elem)}, value: ${this.value}, toggler elem value: ${toggler.elem.value}`)

          toggler.set(this.value === toggler.elem.value)
        })
      })
  }
}

class CheckboxToggler extends Toggler {
  _bind () {
    this.elem.addEventListener('change', (e) => this.set(this.elem.checked))
  }
}

// Toggles content when selected value is included in `values` passed in `opts`
export class SelectizeToggler extends Toggler {
  _bind () {
    this.elem.selectize.on('change', (newValue) => this.set(this.opts.values.includes(newValue)))
  }
}

// Toggles content when selected value is included in `values` passed in `opts`

export class SelectToggler extends Toggler {
  _bind () {
    this.elem.addEventListener('change', (e) => {
      const newValue = this.elem.value
      const newState = this.opts.values.includes(newValue)

      this.logger.debug(`SelectToggler: newValue: ${newValue} for ${nodeToString(this.elem)}`)
      this.logger.debug(`SelectToggler: set(${newState}) on ${nodeToString(this.container)} (from ${nodeToString(this.container)})`)

      this.set(newState)
    })
  }
}


// Bind a toggler for select option that have `data-toggle='<css-selectors>'`
//
// This can be used to have a select option that toggles multiple elements when selected

// This is the toggler for selectize options. It is bound in selects.js
export class SelectizeOptionToggler {
  constructor (select) {
    this.select = select
    this.logger = createLogger('SelectizeOptionToggler')
  }

  bind () {
    const elemsToValues = new SelectElementsToValues
    const children = this.select.selectize.revertSettings.$children
    const options = [...children.find('option').add(children.filter('option'))] // Find options in optgroups too

    options
      .filter((option) => option.dataset.toggle)
      .forEach((option) => {
        elemsToValues.add(option.dataset.toggle, option.value)
      })

    this.logger.debug(`SelectizeOptionToggler: Built mapping for ${this.select.id}:\n\n${elemsToValues.toString()}`)

    Object.entries(elemsToValues.mapping).forEach((entry) => {
      const [elem, values] = entry
      // TODO: Should not be using document.querySelector as this causes issues with the
      // loader. It should be using the content from the loader
      const toggledContent = document.querySelector(elem)

      new SelectizeToggler(this.select, toggledContent, null, {values: values})
    })
  }
}

// This is the toggler for non-selectize select options
class SelectOptionToggler {
  constructor (content, select) {
    this.content = content
    this.select = select
    this.logger = createLogger('SelectOptionToggler')
  }

  bind () {
    const elemsToValues = new SelectElementsToValues
    const options = Array.from(this.select.children)
    // TODO: How to get options from optgroups?

    options
      .filter((option) => option.dataset.toggle)
      .forEach((option) => {
        elemsToValues.add(option.dataset.toggle, option.value)
      })

    this.logger.debug(`SelectOptionToggler: Built mapping for ${this.select.id}:\n\n${elemsToValues.toString()}`)

    Object.entries(elemsToValues.mapping).forEach((entry) => {
      const [elem, values] = entry
      // TODO: Should not be using document.querySelector as this causes issues with the
      // loader. It should be using the content from the loader
      const toggledContent = document.querySelector(elem)

      new SelectToggler(this.select, toggledContent, null, { values: values })
    })
  }
}


// Used to flatten the mapping of CSS selectors to toggle to selected options.
//
// This is because the elements to be toggled can be on multiple select options
// in different combinations and we only want one toggler per toggled element,
// or they will fight each other
class SelectElementsToValues {
  constructor () {
    this.mapping = {}
  }

  add (selector, value) {
    selector.split(',').forEach((newSelector) => {
      newSelector = newSelector.trim()
      this.mapping[newSelector] ||= []
      this.mapping[newSelector].push(value)
    })
  }

  toString () {
    return Object.entries(this.mapping).map(([key, values]) => `${key}: ${values.join(', ')}`).join('\n')
  }
}
class TogglerBinder {
  constructor(content) {
    this.logger = createLogger('TogglerBinding')
    this.content = content
  }

  bind() {
    this.content.querySelectorAll('[data-toggle]').forEach((elem) => {  
      if(!elem.toggleEnabled && elem.dataset.toggle.length > 0) {
        elem.toggleEnabled = true
        let toggledContent = this.content.querySelector(elem.dataset.toggle)

        const options = {}
        options.animation = elem.dataset.toggleAnimation
        options.scrollTarget = elem.dataset.scrollTarget
        options.pureCss = elem.dataset.togglePureCss
  
        if(elem.dataset.toggleWithin) {
          toggledContent = elem.closest(elem.dataset.toggleWithin).querySelector(elem.dataset.toggle)
        }
  
        if(elem.nodeName === 'INPUT' && elem.type === 'radio') {
          elem.toggler = new RadioToggler(elem, toggledContent, null, options)

          this.logger.debug(`RadioToggler added to '${nodeToString(elem)}' for '${nodeToString(toggledContent)}'`)
        } else if(elem.nodeName === 'INPUT' && elem.type === 'checkbox') {
          elem.toggler = new CheckboxToggler(elem, toggledContent, null, options)

          this.logger.debug(`CheckboxToggler added to '${nodeToString(elem)}' for '${nodeToString(toggledContent)}'`)
        } else if(elem.nodeName === 'OPTION') {
          const select = elem.parentElement
      
          // Selectize togglers are bound in selects.js
          if(!select.toggler && select.classList.contains('no-js')) {
            select.toggler = new SelectOptionToggler(this.content,select)
            select.toggler.bind()
          }      
        } else if(elem.nodeName !== 'OPTION') {
          elem.toggler = new Toggler(elem, toggledContent, null, options)

          this.logger.debug(`Toggler added to '${nodeToString(elem)}' for '${nodeToString(toggledContent)}'`)
        }
      }
    })  
  }
}
Loader.functional((content, resolve) => {
  new TogglerBinder(content).bind()

  resolve()
})
